import React, { createContext, useContext, useMemo } from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { TYPOGRAPHY } from "../styles/theme";
import { DEFAULT_PRIMARY_COLOR, DEFAULT_SECONDARY_COLOR } from "../consts";
const AppThemeContext = createContext(undefined);
export function useAppTheme() {
    const ctx = useContext(AppThemeContext);
    if (!ctx) {
        throw new Error("useAppTheme must be used within an AppThemeProvider");
    }
    return ctx;
}
export function AppThemeProvider({ children }) {
    // Same query key as everywhere else — when UserInfo invalidates it,
    // this hook refetches too and the theme rebuilds automatically.
    const primaryColor = DEFAULT_PRIMARY_COLOR; // warm brown
    const secondaryColor = DEFAULT_SECONDARY_COLOR; // terracotta
    const theme = useMemo(() => {
        return createTheme({
            palette: {
                mode: "light",
                primary: {
                    main: primaryColor,
                    light: "#8A7461",
                    dark: "#362A20",
                    contrastText: "#FAF6EF",
                },
                secondary: {
                    main: secondaryColor,
                    light: "#C99A7A",
                    dark: "#80563F",
                    contrastText: "#FFFFFF",
                },
                background: {
                    default: "#FAF6EF",
                    paper: "#6BAED6",
                },
                text: {
                    primary: "#2B241E",
                    secondary: "#766B60",
                    disabled: "#A69A8D",
                },
                divider: "#DDD2C4",
                warning: {
                    main: "#C58B3A",
                },
                error: {
                    main: "#A65D52",
                },
                success: {
                    main: "#71856B",
                },
            },
            typography: TYPOGRAPHY,
            components: {
                MuiCssBaseline: {
                    styleOverrides: {
                        body: {
                            backgroundColor: "#FAF6EF",
                            color: "#2B241E",
                        },
                        "::selection": {
                            backgroundColor: secondaryColor,
                            color: "#FFFFFF",
                        },
                    },
                },
                MuiButton: {
                    defaultProps: {
                        color: "secondary",
                        disableElevation: true,
                    },
                    styleOverrides: {
                        root: {
                            borderRadius: 2,
                            padding: "10px 22px",
                            fontWeight: 500,
                            letterSpacing: "0.1em",
                            textTransform: "uppercase",
                            transition: "all 180ms ease",
                        },
                        containedSecondary: {
                            "&:hover": {
                                transform: "translateY(-1px)",
                            },
                        },
                        outlinedSecondary: {
                            borderWidth: 1,
                            "&:hover": {
                                borderWidth: 1,
                            },
                        },
                    },
                },
                MuiCard: {
                    styleOverrides: {
                        root: {
                            backgroundColor: "#F3ECE2",
                            border: "1px solid #DDD2C4",
                            borderRadius: 2,
                            boxShadow: "0 8px 30px rgba(43, 36, 30, 0.06)",
                        },
                    },
                },
                MuiPaper: {
                    styleOverrides: {
                        root: {
                            backgroundImage: "none",
                        },
                        rounded: {
                            borderRadius: 2,
                        },
                    },
                },
                MuiTextField: {
                    defaultProps: {
                        color: "secondary",
                        variant: "outlined",
                    },
                },
                MuiOutlinedInput: {
                    styleOverrides: {
                        root: {
                            borderRadius: 2,
                            backgroundColor: "#FFFDF9",
                            "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#D8CCBD",
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#A99784",
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: secondaryColor,
                                borderWidth: 1,
                            },
                        },
                    },
                },
                MuiInputLabel: {
                    styleOverrides: {
                        root: {
                            color: "#766B60",
                            "&.Mui-focused": {
                                color: secondaryColor,
                            },
                        },
                    },
                },
                MuiLink: {
                    defaultProps: {
                        underline: "none",
                    },
                    styleOverrides: {
                        root: {
                            color: "inherit",
                            transition: "color 180ms ease",
                            "&:hover": {
                                color: secondaryColor,
                            },
                        },
                    },
                },
                MuiDivider: {
                    styleOverrides: {
                        root: {
                            borderColor: "#DDD2C4",
                        },
                    },
                },
                MuiIconButton: {
                    defaultProps: {
                        color: "secondary",
                    },
                    styleOverrides: {
                        root: {
                            borderRadius: 2,
                            "&:hover": {
                                backgroundColor: "rgba(128, 86, 63, 0.08)",
                            },
                        },
                    },
                },
                MuiCheckbox: {
                    defaultProps: {
                        color: "secondary",
                    },
                },
                MuiRadio: {
                    defaultProps: {
                        color: "secondary",
                    },
                },
                MuiSwitch: {
                    defaultProps: {
                        color: "secondary",
                    },
                },
                MuiChip: {
                    defaultProps: {
                        color: "secondary",
                    },
                    styleOverrides: {
                        root: {
                            borderRadius: 2,
                            fontFamily: "'Jost', sans-serif",
                            letterSpacing: "0.04em",
                        },
                    },
                },
                MuiTabs: {
                    defaultProps: {
                        textColor: "secondary",
                        indicatorColor: "secondary",
                    },
                    styleOverrides: {
                        indicator: {
                            height: 1,
                        },
                    },
                },
                MuiTab: {
                    styleOverrides: {
                        root: {
                            minWidth: "auto",
                            padding: "10px 16px",
                            fontFamily: "'Jost', sans-serif",
                            fontSize: "0.8rem",
                            fontWeight: 500,
                            letterSpacing: "0.08em",
                            textTransform: "uppercase",
                        },
                    },
                },
                MuiDialog: {
                    styleOverrides: {
                        paper: {
                            backgroundColor: "#FAF6EF",
                            borderRadius: 2,
                            boxShadow: "0 20px 60px rgba(43, 36, 30, 0.15)",
                        },
                    },
                },
                MuiMenu: {
                    styleOverrides: {
                        paper: {
                            backgroundColor: "#FAF6EF",
                            border: "1px solid #DDD2C4",
                            borderRadius: 2,
                            boxShadow: "0 12px 35px rgba(43, 36, 30, 0.1)",
                        },
                    },
                },
                MuiTooltip: {
                    styleOverrides: {
                        tooltip: {
                            backgroundColor: "#2B241E",
                            fontFamily: "'Jost', sans-serif",
                            fontSize: "0.75rem",
                            letterSpacing: "0.03em",
                            borderRadius: 2,
                        },
                    },
                },
            },
        });
    }, [primaryColor, secondaryColor]);
    const value = {
        primaryColor,
        secondaryColor,
        isDefault: primaryColor === DEFAULT_PRIMARY_COLOR &&
            secondaryColor === DEFAULT_SECONDARY_COLOR,
    };
    return (React.createElement(AppThemeContext.Provider, { value: value },
        React.createElement(ThemeProvider, { theme: theme }, children)));
}
